import { useEffect, useState } from "react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { fetchReviews, postReview } from "@/lib/api";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";

const Stars = ({ value, size = 14 }) => (
  <div className="flex gap-1 text-gold" aria-label={`${value} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} fill={i < value ? "currentColor" : "none"} strokeWidth={1.5} />
    ))}
  </div>
);

const GoogleStars = () => (
  <div className="flex gap-1 text-gold" aria-label="4.5 out of 5 stars on Google">
    {[1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" />)}
    <span className="relative inline-block leading-none">
      <Star size={16} fill="none" strokeWidth={1.5} />
      <span className="absolute inset-0 overflow-hidden" style={{ width: "50%" }} aria-hidden="true">
        <Star size={16} fill="currentColor" strokeWidth={1.5} />
      </span>
    </span>
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchReviews()
      .then((d) => setReviews(d.reviews))
      .catch(() => toast.error("Could not load reviews right now"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const saved = await postReview(form);
      setReviews((r) => [saved, ...r]);
      setForm({ name: "", rating: 5, text: "" });
      toast.success("Thank you! Your review is now live.");
    } catch {
      toast.error("Please check your review and try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="py-28 md:py-36 bg-blush/30 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <SectionHeading overline="Guest Book" title="Sweet Words" />
          <Reveal delay={0.1} className="mt-8">
            <div className="flex items-end gap-4">
              <span data-testid="reviews-average" className="font-display font-black text-6xl text-teal">4.5</span>
              <div className="pb-2">
                <GoogleStars />
                <p className="text-xs uppercase tracking-[0.2em] text-teal/60 mt-1">on Google Reviews · as at July 2026</p>
              </div>
            </div>
            <a href="https://www.google.com/maps/search/Morelli's+Ice+Cream+Parlour+Broadstairs" target="_blank" rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-gold hover:text-teal transition-colors duration-300 underline underline-offset-4">
              Read every review on Google
            </a>
          </Reveal>
          <div className="mt-10 columns-1 sm:columns-2 gap-6 space-y-6">
            {reviews.slice(0, 6).map((r, i) => (
              <Reveal key={r.id} delay={Math.min(i * 0.06, 0.3)} className="break-inside-avoid">
                <blockquote data-testid={`review-card-${i}`} className="bg-white rounded-[1.5rem] p-6 shadow-md shadow-teal/10 border border-gold/20">
                  <Stars value={r.rating} />
                  <p className="mt-4 text-sm leading-relaxed text-teal/80">&ldquo;{r.text}&rdquo;</p>
                  <footer className="mt-4 font-display italic text-teal">{r.name}</footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15} className="lg:sticky lg:top-32">
            <form onSubmit={submit} data-testid="review-form" className="bg-teal text-cream rounded-[2rem] p-8 md:p-10 shadow-2xl shadow-teal/30">
              <h3 className="font-display font-bold text-3xl">Leave Your Review</h3>
              <p className="text-sm text-cream/70 mt-2">Been to see us? Tell the world how it tasted.</p>

              <label className="block mt-8 text-xs uppercase tracking-[0.25em] text-cream/60" htmlFor="review-name">Your name</label>
              <input id="review-name" data-testid="review-name-input" required maxLength={60}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-cream/40 focus:border-gold focus:outline-none py-2 text-lg placeholder:text-cream/30 transition-colors duration-300"
                placeholder="e.g. Florence from Margate" />

              <span className="block mt-8 text-xs uppercase tracking-[0.25em] text-cream/60">Your rating</span>
              <div className="flex gap-2 mt-2" role="radiogroup" aria-label="Star rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setForm({ ...form, rating: n })}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`} aria-pressed={form.rating === n}
                    className="text-gold hover:scale-125 transition-transform duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded">
                    <Star size={28} fill={n <= form.rating ? "currentColor" : "none"} strokeWidth={1.5} />
                  </button>
                ))}
              </div>

              <label className="block mt-8 text-xs uppercase tracking-[0.25em] text-cream/60" htmlFor="review-text">Your review</label>
              <textarea id="review-text" data-testid="review-text-input" required minLength={4} maxLength={600} rows={3}
                value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full bg-transparent border-b border-cream/40 focus:border-gold focus:outline-none py-2 text-lg placeholder:text-cream/30 resize-none transition-colors duration-300"
                placeholder="The sundae that changed your life..." />

              <button type="submit" data-testid="review-submit-button" disabled={sending}
                className="mt-10 w-full rounded-full bg-gold text-teal-deep font-semibold uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 hover:bg-cream transition-colors duration-300 active:scale-95 disabled:opacity-60">
                {sending ? "Sharing..." : <>Share Your Story <Send size={16} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
